/**
 * Modèle fonctionnel Plan2Cost — itération 4 (THÈME 1 : gestion des hypothèses).
 *
 * Option B (décision PO) : pas d'objet « jeu complet ». L'utilisateur gère des
 * JEUX d'hypothèses INDÉPENDANTS, rangés PAR FAMILLE (ex. « Tracé A », « Tracé B »).
 * Un Plan de dev (thème 2) choisira ensuite 1 jeu par famille, combinés directement.
 * Un même jeu peut servir à plusieurs plans. Il n'existe PAS d'entité configuration globale.
 *
 * Chaîne de traçabilité : Projet → Hypothèses (jeux, par famille) → Plan de dev → Résultat.
 */

export type Family = 'demande' | 'trace' | 'developpement' | 'phasage' | 'importance' | 'pointsDepart';

/**
 * Comportement carte d'une famille.
 * - `none` : formulaire pleine largeur, AUCUNE carte (Demande, Développement, Phasage).
 * - `routes` / `mandatory` / `startpoints` : carte affichée À LA DEMANDE (Tracé, Importance, Points de départ).
 */
export type FamilyMap = 'routes' | 'mandatory' | 'startpoints' | 'none';

export interface FamilyMeta {
  key: Family;
  label: string;
  title: string;
  question: string;
  /** Résumé court pour la tuile d'accueil. */
  tagline: string;
  /** Ligature Material Symbols (rail / tuiles / liste compacte). */
  icon: string;
  map: FamilyMap;
}

export const FAMILIES: FamilyMeta[] = [
  { key: 'demande', label: 'Demande', title: 'Demande', question: 'Quelle consommation retenir et comment évolue-t-elle ?', tagline: 'Consommation, raccordement, érosion.', icon: 'bolt', map: 'none' },
  { key: 'trace', label: 'Tracé', title: 'Tracé', question: 'Par où passe le réseau (routes autorisées, pondérées, existant) ?', tagline: 'Pondération des routes, réseau existant.', icon: 'route', map: 'routes' },
  { key: 'developpement', label: 'Développement', title: 'Développement', question: 'Jusqu’où développer le réseau (km, MWh, objectifs) ?', tagline: 'Km de réseau, MWh, autres objectifs.', icon: 'trending_up', map: 'none' },
  { key: 'phasage', label: 'Phasage', title: 'Phasage', question: 'À quel rythme raccorder (km/an, MWh/an, clients/an) ?', tagline: 'Rythme annuel de raccordement.', icon: 'event', map: 'none' },
  { key: 'importance', label: 'Importance clients', title: 'Importance des clients', question: 'Quels clients sont obligatoires ou prioritaires ?', tagline: 'Bâtiments obligatoires, densification.', icon: 'star', map: 'mandatory' },
  { key: 'pointsDepart', label: 'Points de départ', title: 'Points de départ', question: 'D’où part le réseau et avec quelle production ?', tagline: 'Points de départ, chaufferies, capacités.', icon: 'my_location', map: 'startpoints' },
];

interface SetBase {
  id: string;
  name: string;
  updatedAt: string;
  /** Incrémenté à chaque enregistrement : sert à détecter l'obsolescence des plans. */
  version: number;
}

/** Demande : coefficient sur la consommation, taux de raccordement, érosion. */
export interface DemandeSet extends SetBase {
  kind: 'demande';
  consoCoef: number;
  connectionRate: number;
  erosion: number;
}

/** Tracé : pondération des routes (interdit / obligatoire / xN) + réseau existant. */
export interface TraceSet extends SetBase {
  kind: 'trace';
  forbiddenRoads: number;
  mandatoryRoads: number;
  weightedRoads: number;
  useExistingNetwork: boolean;
}

/** Développement : km de réseau, MWh, objectif prioritaire + autres objectifs (texte). */
export interface DevSet extends SetBase {
  kind: 'developpement';
  targetKm: number;
  targetMWh: number;
  objective: 'km' | 'mwh' | 'mixte';
  otherObjective: string;
}

/** Phasage : rythmes annuels de développement. */
export interface PhasageSet extends SetBase {
  kind: 'phasage';
  kmPerYear: number;
  mwhPerYear: number;
  clientsPerYear: number;
}

/** Importance des clients : bâtiments obligatoires (carte), densification. */
export interface ImportanceSet extends SetBase {
  kind: 'importance';
  mandatoryBuildings: string[];
  densification: 'faible' | 'moyenne' | 'forte';
  priority: 'obligatoire' | 'densification' | 'mixte';
}

/** Points de départ : points de départ réseau (carte) + chaufferies, capacités et dates. */
export interface PointsDepartSet extends SetBase {
  kind: 'pointsDepart';
  startPoints: number;
  boilerRooms: 'existantes' | 'creees' | 'mixte';
  commissioningYear: string;
  capacityMW: number;
  backupYear: string;
  backupCapacityMW: number;
}

export type AnySet = DemandeSet | TraceSet | DevSet | PhasageSet | ImportanceSet | PointsDepartSet;

export interface SetsByFamily {
  demande: DemandeSet[];
  trace: TraceSet[];
  developpement: DevSet[];
  phasage: PhasageSet[];
  importance: ImportanceSet[];
  pointsDepart: PointsDepartSet[];
}

export type PlanStatus = 'draft' | 'running' | 'done' | 'stale' | 'failed';
export type StoredStatus = 'draft' | 'running' | 'done' | 'failed';
export type PlanBrick = 'hypotheses' | 'buildings' | 'trace' | 'phasing' | 'recap' | 'locals';

export interface TechnicalPoint {
  id: string;
  coordinates: [number, number];
  buildingId?: string;
  status: 'pending' | 'validated' | 'replaced';
  fileName: string;
  originPlan?: string;
  originalStatus?: TechnicalPoint['status'];
}

export interface BuildingSelection {
  id: string;
  name: string;
  buildingIds: string[];
  updatedAt: string;
}

export interface PlanResults {
  connectedBuildings: number;
  /** Identifiants fonctionnels des bâtiments raccordés (affichés sur la carte). */
  connectedIds: string[];
  networkKm: number;
  deliveredGWh: number;
  erodedGWh: number;
  density: number;
  durationYears: number;
  triggered: number;
}

/** Snapshot figé au calcul : la combinaison exacte de jeux qui a produit le résultat. */
export interface PlanSnapshot {
  sets: Record<Family, { id: string; version: number }>;
  computedAt: string;
  inputs?: import('./construction').PlanInputs;
  results?: PlanResults;
}

/**
 * Plan de développement : référence un jeu par famille et conserve les choix V1.
 */
export interface Plan {
  id: string;
  name: string;
  energy: 'chaud' | 'froid';
  setIds: Record<Family, string>;
  status: StoredStatus;
  progress: number;
  updatedAt: string;
  selectionIds?: string[];
  selectionSources?: BuildingSelection[];
  manualBuildingIds?: string[];
  excludedBuildingIds?: string[];
  buildingIds?: string[];
  routeMode?: 'auto' | 'manual';
  routePipeIndexes?: number[];
  buildingPhases?: Record<string, string>;
  phasingMode?: 'manual' | 'auto-simulation' | 'mixed';
  pipePhaseOverrides?: Record<number, string>;
  technicalPoints?: TechnicalPoint[];
  configurationChanged?: boolean;
  results?: PlanResults;
  snapshot?: PlanSnapshot;
  buildingsConfirmed?: boolean;
  traceCalculated?: boolean;
  densityCalculated?: boolean;
  densityCorrection?: string;
  calculationSection?: 'trace' | 'density' | 'phasing';
  calculationError?: string;
  invalidations?: string[];
  archives?: { snapshot: PlanSnapshot; results?: PlanResults }[];
}

/** Paramètres du projet : certain / réalité, lecture seule dans ce prototype. */
export interface ProjectParams {
  perimeter: string;
  existingNetwork: string;
  weatherStation: string;
  dju: number;
  temperatureProfile: string;
  heatRatio: string;
  coldRatio: string;
  startDate: string;
  endDate: string;
  detourCoef: number;
  networkKind: 'Existant' | 'Nouveau';
  selectionsImported: number;
}

export interface Project {
  id: string;
  name: string;
  perimeterLabel: string;
  snapshotDate: string;
  owner: string;
  /** Nombre de bâtiments d'UrbanMap modifiés depuis la copie (dérive). */
  umDriftCount: number;
  /** Sous-échantillon de bâtiments (mock : varie les résultats d'un projet à l'autre). */
  skip: number;
  params: ProjectParams;
  sets: SetsByFamily;
  selections: BuildingSelection[];
  plans: Plan[];
  technicalPoints?: TechnicalPoint[];
}

export type Tab = 'hypotheses' | 'selections' | 'plans' | 'optimization' | 'params' | 'exchange';

export type Navigation =
  | { view: 'projects' }
  | { view: 'project'; projectId: string; tab: Tab; family?: Family; setId?: string; edit?: boolean; planId?: string; brick?: PlanBrick };

export type Draft = { kind: 'set'; value: AnySet };
